<template>
  <div
    class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]"
  >
    <div class="w-full">
      <h2 class="page-title font-sans">Measuring Impact: Outcomes</h2>

      <div v-if="isLoading" class="text-gray-600 font-sans py-4">
        <p>Loading data...</p>
      </div>
      <div
        v-else-if="!authStore.user?.organization_id"
        class="p-4 bg-yellow-50 font-sans border border-yellow-200 rounded-md mb-4"
      >
        <p class="text-amber-700">Unable to load organization data. Please try refreshing the page.</p>
        <button
          @click="authStore.fetchUserData"
          class="bg-amber-600 font-sans text-white px-4 py-1 rounded-md mt-2"
        >
          Retry Loading Organization Data
        </button>
      </div>
      <div v-else>
        <div class="bg-white rounded-xl mb-6">
          <p class="text-gray-700 mb-[32px]">
            Outcomes are meaningful, long-term changes your program aims to create in the community, such as improved health, increased graduation rates, or changes in behavior or policy. These reflect the deeper purpose of your work.
          </p>
        </div>

        <QuestionField
          v-model="answerState.userAnswer"
          :question="questionText"
          placeholder="Enter your program's outcomes..."
        />
        <p v-if="errorMessage" class="text-red-500 font-sans">
          {{ errorMessage }}
        </p>
        <p v-if="v$.userAnswer.$error" class="text-red-500 font-sans">
          {{ v$.userAnswer.$errors[0].$message }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, computed, watch } from "vue";
import QuestionField from "@/components/QuestionField.vue";
import { useAuthStore } from "@/stores/auth";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import global from "@/global";
import { useStepValidation } from "@/composables/useStepValidation";

const isLoading = ref(true);
const questionText = ref("");
const errorMessage = ref("");
const answerState = reactive({ userAnswer: "" });
const lastSavedValue = ref("");

// Validation rules
const rules = {
  userAnswer: {
    required: helpers.withMessage("Answer is required!", required),
  },
};
const v$ = useVuelidate(rules, answerState);

// Fetching the auth store
const authStore = useAuthStore();

// Watch for changes in the answer to validate in real-time
watch(
  () => answerState.userAnswer,
  async (newValue) => {
    await v$.value.$validate();
  },
  { immediate: true }
);

// Define categoryId and questionId as refs
const categoryId = ref("");
const questionId = ref("");

// Custom validation function for the useStepValidation composable
const validateStep = async () => {
  // First trigger Vuelidate validation
  await v$.value.$validate();

  // If there are validation errors, immediately return false
  if (v$.value.$invalid) {
    return false;
  }

  // If we have unsaved changes, save them first
  if (answerState.userAnswer !== lastSavedValue.value) {
    try {
      await saveUserAnswer();
      return true;
    } catch (error) {
      console.error("Failed to save answer:", error);
      errorMessage.value =
        "Please ensure your answer is saved before proceeding.";
      return false;
    }
  }

  // Return true if everything is valid and saved
  return !v$.value.$invalid;
};

// Use the step validation composable
useStepValidation({
  initialValidState: false, // Start with invalid state
  validateFn: validateStep,
});

// Fetching question data directly using fetch API
async function fetchQuestionData() {
  try {
    const res = await fetch(`${global.API_URL}/questions/measuring_impact_outcomes`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    
    // Check if the response has the expected structure
    if (!data || !data.question) {
      throw new Error('Invalid response structure from API');
    }

    // Set the question text and IDs
    questionText.value = data.question.title || '';
    categoryId.value = data.question.category_id || '';
    questionId.value = data.question.id || '';

    // Validate that we have all required data
    if (!questionId.value) {
      throw new Error('Question ID is missing from the response');
    }
  } catch (error) {
    console.error('Error fetching question data:', error);
    errorMessage.value = "Failed to load question data. Please try again.";
    isLoading.value = false;
  }
}

// Fetching user answer directly using fetch API
async function getUserAnswer() {
  // Get organization ID from auth store
  const organizationId = authStore.user.organization_id;

  // Check if organizationId and questionId are valid
  if (!organizationId) {
    console.error("Invalid organizationId:", organizationId);
    errorMessage.value = "Invalid organization ID. Please try refreshing the page.";
    return;
  }

  if (!questionId.value) {
    console.error("Invalid questionId:", questionId.value);
    errorMessage.value =
      "Question ID not available. Please try refreshing the page.";
    return;
  }

  try {
    const res = await fetch(
      `${global.API_URL}/answers?question_id=${questionId.value}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${authStore.user.accessToken}`,
        },
      }
    );

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    let answer = data.answer;
    if (answer != null) {
      answerState.userAnswer = answer.answer;
      lastSavedValue.value = answer.answer;
    }
  } catch (error) {
    console.error("Failed to fetch user answer:", error);
    errorMessage.value =
      "Failed to load your previous answer. You can still create a new one.";
  }
}

// Saving user answer directly using fetch API
async function saveUserAnswer() {
  // Validate first
  const isValid = await v$.value.$validate();
  if (!isValid) {
    return false;
  }

  // Don't save if the answer hasn't changed
  if (answerState.userAnswer === lastSavedValue.value) {
    return true;
  }

  // Get organization ID from auth store
  const organizationId = authStore.user.organization_id;

  // Double check that we have a valid organization ID
  if (!organizationId) {
    errorMessage.value = "Invalid organization ID. Please try refreshing the page.";
    return false;
  }

  try {
    const res = await fetch(`${global.API_URL}/answers`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        category_id: categoryId.value,
        question_id: questionId.value,
        answer: answerState.userAnswer,
      }),
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    errorMessage.value = "";
    lastSavedValue.value = answerState.userAnswer;
    return true;
  } catch (error) {
    errorMessage.value = "Failed to save your answer. Please try again.";
    console.error("Failed to save user answer:", error);
    return false;
  }
}

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";

    // First ensure we have the latest user data with organization ID
    await authStore.fetchUserData();
    console.log("Organization ID after fetch:", authStore.user?.organization_id);

    if (!authStore.user?.organization_id) {
      errorMessage.value = "Unable to load organization data. Please try refreshing the page.";
      console.error("No organization ID available");
      return;
    }

    // Then load the question data
    await fetchQuestionData();
    console.log("Question ID after fetch:", questionId.value);

    if (!questionId.value) {
      errorMessage.value = "Unable to load question data. Please try refreshing the page.";
      console.error("No question ID available");
      return;
    }

    // Finally fetch the user's answer
    await getUserAnswer();
    console.log("Answer loaded:", answerState.userAnswer);

  } catch (error) {
    console.error("Error during component initialization:", error);
    errorMessage.value = "An error occurred while loading the data. Please try refreshing the page.";
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
.page-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  color: #414141;
  margin-bottom: 24px;
}
</style> 